import { env } from "@/lib/env";
import { ApiError, UserTypes } from "@/types/types";
import { toast } from "react-toastify";

const CHATS_ENDPOINT = env.NEXT_CHATS_API_URL;
// We now use the internal Next.js API route you created

const EMAIL_REGEX_UNICODE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/u;
const PHONE_REGEX = /^\+[\d ]{7,15}$/;

export async function generalContactHandler(
  ownerSub: string,
  ownerType: UserTypes,
  fullName: string,
  email: string,
  phoneNumber: string,
  message: string,
  idToken: string | null,
  userId: string | null,
): Promise<boolean> {
  try {
    if (idToken === null) {
      if (fullName.length < 2) {
        toast("Name is too short.", { type: "error" });
        return false;
      }
      if (message.length < 10) {
        toast("Message is too short.", { type: "error" });
        return false;
      }
      if (!EMAIL_REGEX_UNICODE.test(email)) {
        toast("Email is not valid.", { type: "error" });
        return false;
      }
      if (!PHONE_REGEX.test(phoneNumber)) {
        toast("Phone number is not valid.", { type: "error" });
        return false;
      }
    }

    const targetEndpoint =
      idToken !== null
        ? `/api/agency/leads/users/auth`
        : `/api/agency/leads/users/guest`;

    const response = await fetch(targetEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // CRITICAL: Send token to Lead API so backend can securely resolve the user's profile
        ...(idToken ? { Authorization: idToken } : {}),
      },
      body: JSON.stringify({
        agent_id: ownerType !== UserTypes.AGENCY ? ownerSub : "",
        agency_id: ownerType === UserTypes.AGENCY ? ownerSub : "",
        user_name: fullName,
        user_phone_number: phoneNumber,
        user_email: email,
        message: message,
        source: "general",
        user_id: userId !== null ? userId : "",
        listing_id: "",
        tour_date: "",
      }),
    });

    if (!response.ok) {
      const apiErr = (await response.json()) as ApiError;
      throw new Error(apiErr.message);
    }

    if (idToken !== null) {
      const chatResponse = await fetch(`/api/chats/createChat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          second_user_sub: ownerSub,
          message_type: "text",
          payload: { text: message },
          is_ai_chat: false,
        }),
      });

      if (!chatResponse.ok) {
        const apiErr = (await chatResponse.json()) as ApiError;
        throw new Error(apiErr.message);
      }
      toast("Message sent successfully.", { type: "success" });
      return true;
    }

    toast("Inquiry sent successfully.", { type: "success" });
    return true;
  } catch (err) {
    const errorMessage =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    toast(
      errorMessage === "Failed to fetch"
        ? "Unable to send informations to agent."
        : errorMessage,
      {
        type: "error",
      },
    );
    return false;
  }
}

export async function listingContactHandler(
  ownerSub: string,
  ownerType: UserTypes,
  fullName: string,
  email: string,
  phoneNumber: string,
  message: string,
  listingId: string,
  idToken: string | null,
  userId: string | null,
): Promise<boolean> {
  try {
    if (idToken === null || ownerType === UserTypes.AGENCY) {
      if (fullName.length < 2) {
        toast("Name is too short.", { type: "error" });
        return false;
      }
      if (message.length < 10) {
        toast("Message is too short.", { type: "error" });
        return false;
      }
      if (!EMAIL_REGEX_UNICODE.test(email)) {
        toast("Email is not valid.", { type: "error" });
        return false;
      }
      if (!PHONE_REGEX.test(phoneNumber)) {
        toast("Phone number is not valid.", { type: "error" });
        return false;
      }
    }

    const targetEndpoint =
      idToken !== null
        ? `/api/agency/leads/users/auth`
        : `/api/agency/leads/users/guest`;

    const response = await fetch(targetEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(idToken ? { Authorization: idToken } : {}),
      },
      body: JSON.stringify({
        listing_id: listingId,
        agent_id: ownerType !== UserTypes.AGENCY ? ownerSub : "",
        agency_id: ownerType === UserTypes.AGENCY ? ownerSub : "",
        user_name: fullName,
        user_phone_number: phoneNumber,
        user_email: email,
        message: message,
        source: "text",
        user_id: userId !== null ? userId : "",
        tour_date: "",
      }),
    });

    if (!response.ok) {
      const apiErr = (await response.json()) as ApiError;
      throw new Error(apiErr.message);
    }

    if (idToken !== null && ownerType !== UserTypes.AGENCY) {
      const chatResponse = await fetch(`/api/chats/createChat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          second_user_sub: ownerSub,
          message_type: "listing",
          payload: {
            listing_ids: [listingId],
            text: message,
          },
          is_ai_chat: false,
        }),
      });

      if (!chatResponse.ok) {
        const apiErr = (await chatResponse.json()) as ApiError;
        throw new Error(apiErr.message);
      }
      toast("Message sent successfully.", { type: "success" });
      return true;
    }

    toast("Inquiry sent successfully.", { type: "success" });
    return true;
  } catch (err) {
    const errorMessage =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    toast(
      errorMessage === "Failed to fetch"
        ? "Unable to send informations to agent."
        : errorMessage,
      {
        type: "error",
      },
    );
    return false;
  }
}

export async function locationContactHandler(
  ownerSub: string,
  ownerType: UserTypes,
  fullName: string,
  email: string,
  phoneNumber: string,
  message: string,
  listingId: string,
  idToken: string | null,
  userId: string | null,
): Promise<boolean> {
  try {
    if (idToken === null || ownerType === UserTypes.AGENCY) {
      if (fullName.length < 2) {
        toast("Name is too short.", { type: "error" });
        return false;
      }
      if (message.length < 10) {
        toast("Message is too short.", { type: "error" });
        return false;
      }
      if (!EMAIL_REGEX_UNICODE.test(email)) {
        toast("Email is not valid.", { type: "error" });
        return false;
      }
      if (!PHONE_REGEX.test(phoneNumber)) {
        toast("Phone number is not valid.", { type: "error" });
        return false;
      }
    }

    const targetEndpoint =
      idToken !== null
        ? `/api/agency/leads/users/auth`
        : `/api/agency/leads/users/guest`;

    const response = await fetch(targetEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(idToken ? { Authorization: idToken } : {}),
      },
      body: JSON.stringify({
        listing_id: listingId,
        agent_id: ownerType !== UserTypes.AGENCY ? ownerSub : "",
        agency_id: ownerType === UserTypes.AGENCY ? ownerSub : "",
        user_name: fullName,
        user_phone_number: phoneNumber,
        user_email: email,
        message: message,
        source: "location",
        user_id: userId !== null ? userId : "",
        tour_date: "",
      }),
    });

    if (!response.ok) {
      const apiErr = (await response.json()) as ApiError;
      throw new Error(apiErr.message);
    }

    if (idToken !== null && ownerType !== UserTypes.AGENCY) {
      const chatResponse = await fetch(`/api/chats/createChat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          second_user_sub: ownerSub,
          message_type: "listing",
          payload: {
            listing_ids: [listingId],
            text: message,
          },
          is_ai_chat: false,
        }),
      });

      if (!chatResponse.ok) {
        const apiErr = (await chatResponse.json()) as ApiError;
        throw new Error(apiErr.message);
      }
      toast("Message sent successfully.", { type: "success" });
      return true;
    }

    toast("Inquiry sent successfully.", { type: "success" });
    return true;
  } catch (err) {
    const errorMessage =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    toast(
      errorMessage === "Failed to fetch"
        ? "Unable to send informations to agent."
        : errorMessage,
      {
        type: "error",
      },
    );
    return false;
  }
}

export async function tourContactHandler(
  ownerSub: string,
  ownerType: UserTypes,
  fullName: string,
  email: string,
  phoneNumber: string,
  message: string,
  listingId: string,
  listingTitle: string,
  idToken: string | null,
  userId: string | null,
  tourDate: string,
): Promise<boolean> {
  try {
    if (idToken === null || ownerType === UserTypes.AGENCY) {
      if (fullName.length < 2) {
        toast("Name is too short.", { type: "error" });
        return false;
      }
      if (message.length < 10) {
        toast("Message is too short.", { type: "error" });
        return false;
      }
      if (!EMAIL_REGEX_UNICODE.test(email)) {
        toast("Email is not valid.", { type: "error" });
        return false;
      }
      if (!PHONE_REGEX.test(phoneNumber)) {
        toast("Phone number is not valid.", { type: "error" });
        return false;
      }
      if (tourDate === "") {
        toast("Tour date is not valid.", { type: "error" });
        return false;
      }
    }

    let strictRFC3339Date = tourDate;

    if (tourDate) {
      const isoString = new Date(tourDate).toISOString();
      strictRFC3339Date = isoString.split(".")[0] + "Z";
    }

    const targetEndpoint =
      idToken !== null
        ? `/api/agency/leads/users/auth`
        : `/api/agency/leads/users/guest`;

    const response = await fetch(targetEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(idToken ? { Authorization: idToken } : {}),
      },
      body: JSON.stringify({
        listing_id: listingId,
        agent_id: ownerType !== UserTypes.AGENCY ? ownerSub : "",
        agency_id: ownerType === UserTypes.AGENCY ? ownerSub : "",
        user_name: fullName,
        user_phone_number: phoneNumber,
        user_email: email,
        message: message,
        source: "tour",
        user_id: userId !== null ? userId : "",
        tour_date: strictRFC3339Date,
      }),
    });

    if (!response.ok) {
      const apiErr = (await response.json()) as ApiError;
      throw new Error(apiErr.message);
    }

    const leadData = await response.json();

    if (idToken !== null && ownerType !== UserTypes.AGENCY) {
      const tourEvent = {
        event_id: leadData.event_id || `temp-${Date.now()}`, // Fallback just in case
        listing_id: listingId,
        listing_title: listingTitle || "Requested Tour",
        slot_utc: strictRFC3339Date,
        status: "scheduled",
      };

      const chatResponse = await fetch(`/api/chats/createChat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          second_user_sub: ownerSub,
          message_type: "event",
          payload: {
            event: tourEvent,
            text: message,
          },
          is_ai_chat: false,
        }),
      });

      if (!chatResponse.ok) {
        const apiErr = (await chatResponse.json()) as ApiError;
        throw new Error(apiErr.message);
      }
      toast("Message sent successfully.", { type: "success" });
      return true;
    }

    toast("Inquiry sent successfully.", { type: "success" });
    return true;
  } catch (err) {
    const errorMessage =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    // Check if it's our specific calendar conflict errors from the new backend
    if (
      errorMessage === "You already have a scheduled tour." ||
      errorMessage.includes("slot is no longer available")
    ) {
      toast(errorMessage, { type: "error" });
    } else {
      toast(
        errorMessage === "Failed to fetch"
          ? "Unable to send informations to agent."
          : errorMessage,
        {
          type: "error",
        },
      );
    }
    return false;
  }
}
